import * as THREE from 'three';
import { TRAFFIC_COUNT, TRAFFIC_SPEED_MIN, TRAFFIC_SPEED_MAX, TRAFFIC_COLOR } from './constants.js';

/**
 * Simple AI traffic: N cars travelling along OSM road segments.
 * Uses a single InstancedMesh (O(1) draw call regardless of count).
 */
export class Traffic {
  constructor() {
    this._roads  = [];
    this._proj   = null;
    this._elevFn = null;
    this._cars   = [];
    this._dummy  = new THREE.Object3D();
    this._mesh   = this._buildMesh();
  }

  get mesh() { return this._mesh; }

  /** Call after each loadArea() to reinitialise cars on the new road network. */
  load(roads, proj, elevFn) {
    this._roads  = roads.filter(r => r.nodes?.length >= 2 && !r.bridge);
    this._proj   = proj;
    this._elevFn = elevFn;
    this._cars   = [];

    if (!this._roads.length) { this._mesh.count = 0; return; }

    for (let i = 0; i < TRAFFIC_COUNT; i++) {
      const c = this._randomCar();
      if (c) this._cars.push(c);
    }
    this._mesh.count = this._cars.length;

    // Initial matrix placement
    this._cars.forEach((car, i) => {
      const pos = this._carPos(car);
      if (pos) this._setMatrix(i, pos);
    });
    this._mesh.instanceMatrix.needsUpdate = true;
  }

  /** Call once per frame. */
  update(dt) {
    if (!this._cars.length) return;

    for (let i = 0; i < this._cars.length; i++) {
      const car  = this._cars[i];
      const road = this._roads[car.roadIdx];
      if (!road) continue;

      const segLen = this._segmentLen(road, car.segIdx);
      if (segLen < 0.1) { this._advanceCar(car); continue; }

      car.t += (car.speed / segLen) * dt;

      if (car.t >= 1) {
        car.t = 0;
        car.segIdx++;
        if (car.segIdx >= road.nodes.length - 1) {
          const next = this._randomCar();
          if (next) { this._cars[i] = next; continue; }
          car.segIdx = 0;
        }
      }

      const pos = this._carPos(car);
      if (pos) this._setMatrix(i, pos);
    }

    this._mesh.instanceMatrix.needsUpdate = true;
  }

  dispose() {
    this._mesh.geometry.dispose();
    this._mesh.material.dispose();
  }

  // ── Private ────────────────────────────────────────────────────────────────

  _buildMesh() {
    const geo = new THREE.BoxGeometry(1.8, 1.4, 3.8);
    geo.translate(0, 0.7, 0); // pivot at bottom centre
    const mat  = new THREE.MeshLambertMaterial({ color: TRAFFIC_COLOR });
    const mesh = new THREE.InstancedMesh(geo, mat, TRAFFIC_COUNT);
    mesh.castShadow = true;
    mesh.count = 0;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    return mesh;
  }

  _randomCar() {
    if (!this._roads.length) return null;
    const roadIdx = Math.floor(Math.random() * this._roads.length);
    const road    = this._roads[roadIdx];
    const segMax  = road.nodes.length - 2;
    if (segMax < 0) return null;
    return {
      roadIdx,
      segIdx: Math.floor(Math.random() * (segMax + 1)),
      t:      Math.random(),
      speed:  TRAFFIC_SPEED_MIN + Math.random() * (TRAFFIC_SPEED_MAX - TRAFFIC_SPEED_MIN),
    };
  }

  _advanceCar(car) {
    car.segIdx++;
    car.t = 0;
    const road = this._roads[car.roadIdx];
    if (!road || car.segIdx >= road.nodes.length - 1) {
      const next = this._randomCar();
      if (next) Object.assign(car, next);
    }
  }

  _segmentLen(road, segIdx) {
    if (!this._proj) return 0;
    const n0 = road.nodes[segIdx];
    const n1 = road.nodes[segIdx + 1];
    if (!n0 || !n1) return 0;
    const p0 = this._proj.project(n0.lat, n0.lon);
    const p1 = this._proj.project(n1.lat, n1.lon);
    return Math.hypot(p1.x - p0.x, p1.z - p0.z);
  }

  _carPos(car) {
    if (!this._proj) return null;
    const road = this._roads[car.roadIdx];
    if (!road) return null;
    const n0 = road.nodes[car.segIdx];
    const n1 = road.nodes[car.segIdx + 1];
    if (!n0 || !n1) return null;

    const p0 = this._proj.project(n0.lat, n0.lon);
    const p1 = this._proj.project(n1.lat, n1.lon);
    const t  = car.t;
    const x  = p0.x + t * (p1.x - p0.x);
    const z  = p0.z + t * (p1.z - p0.z);
    const y  = this._elevFn ? this._elevFn(x, z) : 0;
    const angle = Math.atan2(p1.x - p0.x, p1.z - p0.z);
    return { x, y, z, angle };
  }

  _setMatrix(i, pos) {
    this._dummy.position.set(pos.x, pos.y, pos.z);
    this._dummy.rotation.set(0, pos.angle, 0);
    this._dummy.updateMatrix();
    this._mesh.setMatrixAt(i, this._dummy.matrix);
  }
}
